import { useState } from "react";
import { Plus, ChevronDown } from "lucide-react";
import { TAG_VALUE_GROUPS } from "@/constants/conditionCategories";
import { DEFAULT_TIME_WINDOW } from "@/constants/segmentOptions";
import { createDefaultCondition, uid } from "./segmentFormUtils";
import {
  INTEREST_GROUP_OPTIONS,
  tagOpForCategory,
  makeTagCondition,
} from "./conditionModel";
import { summarizeSchedule } from "./schedule";
import { ConditionEditor } from "./ConditionEditor";
import OverflowSelect from "./OverflowSelect";
import ScheduleEditor from "./ScheduleEditor";

export function TagCategoryBody({
  category,
  conditions,
  combinator,
  activeTab,
  onSetConditions,
}) {
  const op = tagOpForCategory(category.id, combinator, activeTab);
  const condition = conditions[0] ?? makeTagCondition(category.id, op);
  const selected = Array.isArray(condition.value) ? condition.value : [];

  const commit = (nextValues) => {
    onSetConditions([{ ...condition, op, value: nextValues }]);
  };

  if (category.id === "interests") {
    return (
      <OverflowSelect
        options={INTEREST_GROUP_OPTIONS}
        value={selected}
        onChange={commit}
        creatable
        createHint="Type to add a custom interest"
        placeholder="Select or type intents & interests…"
      />
    );
  }

  const groupValueOf = (group) =>
    selected.find((v) => group.options.some((o) => o.value === v)) ?? null;
  const setGroupValue = (group, val) => {
    const groupValues = new Set(group.options.map((o) => o.value));
    const withoutGroup = selected.filter((v) => !groupValues.has(v));
    commit(val != null ? [...withoutGroup, val] : withoutGroup);
  };

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {TAG_VALUE_GROUPS[category.id].map((group) => (
        <div key={group.key}>
          <p className="mb-1 text-xs font-medium text-(--text-secondary)">
            {group.label}
          </p>
          <OverflowSelect
            isMulti={false}
            options={group.options}
            value={groupValueOf(group)}
            onChange={(val) => setGroupValue(group, val)}
            placeholder="Any"
          />
        </div>
      ))}
    </div>
  );
}

export function MembershipFieldsBody({ fields, conditions, onSetConditions }) {
  const setField = (field, op, values, existing) => {
    const others = conditions.filter((c) => c.field !== field);
    onSetConditions([
      ...others,
      { id: existing?.id ?? uid(), field, op, value: values, disabled: false },
    ]);
  };

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row">
      {fields.map((spec) => {
        const cond = conditions.find((c) => c.field === spec.field);
        const values = Array.isArray(cond?.value) ? cond.value : [];
        return (
          <div key={spec.field} className="min-w-0 flex-1">
            <OverflowSelect
              options={spec.options}
              value={values}
              onChange={(vals) => setField(spec.field, spec.op, vals, cond)}
              placeholder={spec.placeholder}
            />
          </div>
        );
      })}
    </div>
  );
}

export function BehaviorCategoryBody({ conditions, onSetConditions }) {
  const [scheduleOpen, setScheduleOpen] = useState(false);

  const durCond = conditions.find((c) => c.field === "sessionDuration");
  const timeCond = conditions.find((c) => c.field === "createdAt");
  const durOp = durCond?.op === "lte" ? "lte" : "gte";
  const durValue =
    durCond && durCond.value !== "" && durCond.value != null
      ? durCond.value
      : "";
  const schedule = timeCond?.value ?? DEFAULT_TIME_WINDOW;
  const watchSummary = summarizeSchedule(timeCond?.value);

  const setDur = (op, value) => {
    const others = conditions.filter((c) => c.field !== "sessionDuration");
    onSetConditions([
      ...others,
      {
        id: durCond?.id ?? uid(),
        field: "sessionDuration",
        op,
        value,
        disabled: false,
      },
    ]);
  };

  const setSchedule = (next) => {
    const others = conditions.filter((c) => c.field !== "createdAt");
    onSetConditions([
      ...others,
      {
        id: timeCond?.id ?? uid(),
        field: "createdAt",
        op: "timeWindow",
        value: next,
        disabled: false,
      },
    ]);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="w-28 shrink-0 text-sm font-medium text-(--text-secondary)">
          Session duration
        </span>
        <div className="inline-flex overflow-hidden rounded-(--radius-md) border border-(--border-subtle)">
          {[
            ["gte", "≥"],
            ["lte", "≤"],
          ].map(([op, sym]) => (
            <button
              key={op}
              type="button"
              onClick={() => setDur(op, durValue)}
              className={`px-3 py-1.5 text-sm transition-colors ${
                durOp === op
                  ? "bg-(--pastel-violet) text-(--brand-primary)"
                  : "text-(--text-secondary) hover:bg-(--surface-2)"
              }`}
            >
              {sym}
            </button>
          ))}
        </div>
        <input
          type="number"
          min="0"
          value={durValue}
          onChange={(e) => {
            const raw = e.target.value;
            setDur(durOp, raw === "" ? "" : Number(raw));
          }}
          placeholder="seconds"
          className="w-28 rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-1) px-3 py-1.5 text-sm text-(--text-primary)"
        />
        <span className="text-xs text-(--text-muted)">sec</span>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-28 shrink-0 text-sm font-medium text-(--text-secondary)">
            Watch time
          </span>
          <button
            type="button"
            onClick={() => setScheduleOpen((o) => !o)}
            className="inline-flex min-w-56 items-center justify-between gap-2 rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-1) px-3 py-1.5 text-sm hover:border-(--brand-primary)/50"
          >
            <span
              className={
                watchSummary ? "text-(--text-primary)" : "text-(--text-muted)"
              }
            >
              {watchSummary ?? "Any watch time"}
            </span>
            <ChevronDown
              size={14}
              className={`shrink-0 transition-transform ${scheduleOpen ? "rotate-180" : ""}`}
            />
          </button>
        </div>
        {scheduleOpen && (
          <div className="rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-2) p-3">
            <ScheduleEditor value={schedule} onChange={setSchedule} />
          </div>
        )}
      </div>
    </div>
  );
}

export function FieldsCategoryBody({
  category,
  conditions,
  fieldOptionConfig,
  onSetConditions,
}) {
  const updateCond = (id, next) =>
    onSetConditions(conditions.map((c) => (c.id === id ? next : c)));
  const removeCond = (id) =>
    onSetConditions(conditions.filter((c) => c.id !== id));
  const addCond = () =>
    onSetConditions([
      ...conditions,
      createDefaultCondition(category.fields[0], fieldOptionConfig),
    ]);

  return (
    <div className="flex flex-col gap-3">
      {conditions.map((condition) => (
        <ConditionEditor
          key={condition.id}
          condition={condition}
          onChange={(next) => updateCond(condition.id, next)}
          onRemove={() => removeCond(condition.id)}
          fieldOptionConfig={fieldOptionConfig}
          allowedFields={category.fields}
        />
      ))}
      <div>
        <button
          type="button"
          onClick={addCond}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-(--brand-primary) hover:underline"
        >
          <Plus size={14} /> Add condition
        </button>
      </div>
    </div>
  );
}
